import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { Router } from "@angular/router";
import { Observable, Subscription, of, switchMap } from "rxjs";
import { MealService } from "../core/services/meal.service";
import { MealMenuAdminService } from "../core/services/meal-menu-admin.service";
import { LanguageService } from "../core/services/language.service";
import {
  generateDateOptions,
  applyMealMenusToDateOptions,
  getMealData as getMealDataCommon,
  DateOptionWithBadge,
} from "../shared/utils/commenFunctions";
import { pageLoadAnimationBottomToCenter } from "../shared/utils/common-animations";
import { getProgramId, getSelectedLocation } from "../shared/utils/storage-utils";
import { scrollToTop } from "../shared/utils/scroll-utils";

export const APPLY_FUTURE_STORAGE_KEY = "applyFuturePreferences";

export interface ApplyFutureStoredPayload {
  sourceDate: string;
  mealPreferences: Array<Record<string, string | null>>;
  userId: string | number;
  programId: number | null;
}

interface FutureDateCard extends DateOptionWithBadge {
  selected: boolean;
  previouslyBooked: boolean;
  hotMenuText?: string;
  coldMenuText?: string;
}

@Component({
  selector: "app-apply-future-dates",
  standalone: true,
  templateUrl: "./apply-future-dates.component.html",
  styleUrls: ["./apply-future-dates.component.css"],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimationBottomToCenter],
})
export class ApplyFutureDatesComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private readonly router: Router,
    private readonly mealService: MealService,
    private readonly mealMenuAdminService: MealMenuAdminService,
  ) {}

  pageLoadState = "hidden";
  dateOptions: FutureDateCard[] = [];
  sourceDate = "";
  toasterMessage: string | null = null;
  toasterVariant: "success" | "danger" | "warning" | "info" = "info";
  isSubmitting = false;
  private storedPayload: ApplyFutureStoredPayload | null = null;
  private readonly languageService = inject(LanguageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private languageSub?: Subscription;

  get titleText(): string {
    return this.languageService.t("futureDates.title");
  }

  get subtitleText(): string {
    return this.languageService.t("futureDates.subtitle");
  }

  get emptyText(): string {
    return this.languageService.t("futureDates.empty");
  }

  get hotLabel(): string {
    return this.languageService.t("futureDates.hot");
  }

  get coldLabel(): string {
    return this.languageService.t("futureDates.cold");
  }

  get backLabel(): string {
    return this.languageService.t("common.back");
  }

  get applyLabel(): string {
    return this.isSubmitting
      ? this.languageService.t("futureDates.applying")
      : this.languageService.t("futureDates.apply");
  }

  ngOnInit(): void {
    this.languageSub = this.languageService.language$.subscribe(() => this.cdr.markForCheck());
    scrollToTop();
    this.storedPayload = this.readStoredPayload();
    if (!this.storedPayload?.mealPreferences?.length) {
      this.router.navigate(["/meal-preferance"], {
        queryParams: this.storedPayload?.sourceDate
          ? { date: this.storedPayload.sourceDate }
          : undefined,
      });
      return;
    }

    this.sourceDate = this.storedPayload.sourceDate;
    this.dateOptions = this.buildFutureDateCards(this.sourceDate);
    this.loadExistingSelections();
    this.loadMealMenus();
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = "visible";
        scrollToTop();
      }, 50);
    });
  }

  get hasSelectedDates(): boolean {
    return this.dateOptions.some((option) => option.selected);
  }

  toggleDate(index: number): void {
    const option = this.dateOptions[index];
    if (!option) {
      return;
    }
    this.dateOptions = this.dateOptions.map((item, i) =>
      i === index ? { ...item, selected: !item.selected } : item
    );
  }

  handleCardKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggleDate(index);
    }
  }

  onBack(): void {
    this.router.navigate(["/meal-preferance"], {
      queryParams: this.sourceDate ? { date: this.sourceDate } : undefined,
    });
  }

  onApply(): void {
    if (this.isSubmitting) {
      return;
    }
    if (!this.storedPayload) {
      this.toasterMessage = this.languageService.t("futureDates.error.missing");
      this.toasterVariant = "warning";
      return;
    }

    const selectedDates = this.dateOptions
      .filter((option) => option.selected)
      .map((option) => option.dateValue)
      .filter((value): value is string => Boolean(value));

    // Dates the user unchecked that already had a booking — clear those so they no longer show as applied.
    const datesToClear = this.dateOptions
      .filter((option) => !option.selected && option.previouslyBooked && !!option.dateValue)
      .map((option) => option.dateValue as string);

    if (selectedDates.length === 0 && datesToClear.length === 0) {
      this.toasterMessage = this.languageService.t("futureDates.error.selectOne");
      this.toasterVariant = "warning";
      return;
    }

    this.isSubmitting = true;

    const applySelected$: Observable<unknown> =
      selectedDates.length > 0
        ? this.mealService.updateMealInfo(
            this.buildUpdatePayload(selectedDates, this.storedPayload.mealPreferences)
          )
        : of(null);

    const clearDeselected$: Observable<unknown> =
      datesToClear.length > 0
        ? this.mealService.updateMealInfo(this.buildUpdatePayload(datesToClear, []))
        : of(null);

    applySelected$
      .pipe(switchMap(() => clearDeselected$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          sessionStorage.removeItem(APPLY_FUTURE_STORAGE_KEY);
          sessionStorage.setItem("previousRoute", "/scheduled-confirmed");
          this.router.navigate(["/scheduled-confirmed"]);
        },
        error: (error: any) => {
          this.isSubmitting = false;
          this.toasterMessage =
            error?.error?.message ?? this.languageService.t("futureDates.error.update");
          this.toasterVariant = "danger";
        },
      });
  }

  dismissToaster(): void {
    this.toasterMessage = null;
    this.toasterVariant = "info";
  }

  private buildUpdatePayload(
    mealDates: string[],
    mealPreferences: Array<Record<string, string | null>>
  ): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      programId: this.storedPayload?.programId ?? getProgramId(),
      userId: this.storedPayload?.userId,
      mealDate: mealDates,
      mealPreferences,
    };

    const location = getSelectedLocation();
    if (location) {
      payload["locationId"] = location.id;
      payload["locationName"] = location.locationName;
    }
    return payload;
  }

  private readStoredPayload(): ApplyFutureStoredPayload | null {
    try {
      const raw = sessionStorage.getItem(APPLY_FUTURE_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as ApplyFutureStoredPayload;
    } catch {
      return null;
    }
  }

  private buildFutureDateCards(sourceDate: string): FutureDateCard[] {
    // Use the same weekday window as scheduled-confirmed / date-selection (today + 3).
    const allOptions = generateDateOptions();
    const todayValue = allOptions[0]?.dateValue;

    return allOptions
      .filter((option) => option.dateValue && option.dateValue !== todayValue)
      .map((option) => ({
        ...option,
        // Source date is the one the user is applying from — show it selected.
        selected: option.dateValue === sourceDate,
        previouslyBooked: false,
        badgeCount: 0,
        hotMenuText: "",
        coldMenuText: "",
      }));
  }

  /** Pre-select dates that already have meal preferences saved (same as scheduled-confirmed). */
  private loadExistingSelections(): void {
    const userId = this.storedPayload?.userId;
    if (userId == null || !this.dateOptions.length) {
      return;
    }

    getMealDataCommon(
      this.mealService,
      String(userId),
      this.dateOptions,
      (response: any) => {
        const bookedDates = this.extractBookedDates(response);
        this.dateOptions = this.dateOptions.map((option) => {
          const isBooked = !!(option.dateValue && bookedDates.has(option.dateValue));
          const isSource = option.dateValue === this.sourceDate;
          return {
            ...option,
            previouslyBooked: isBooked,
            // Keep already-applied dates and the source date selected by default.
            selected: isBooked || isSource,
          };
        });
      },
      () => {
        // Keep source date selected even if meal lookup fails.
        if (!this.sourceDate) {
          return;
        }
        this.dateOptions = this.dateOptions.map((option) => ({
          ...option,
          selected: option.dateValue === this.sourceDate || option.selected,
        }));
      }
    );
  }

  private extractBookedDates(response: any): Set<string> {
    const booked = new Set<string>();
    const results: any[] = Array.isArray(response?.results)
      ? response.results
      : Array.isArray(response)
        ? response
        : [];

    for (const item of results) {
      const dateValue = item?.mealDate ?? item?.date;
      if (!dateValue) {
        continue;
      }
      const prefs = item?.mealPreference;
      const hasPreference =
        Array.isArray(prefs) &&
        prefs.some(
          (pref: any) =>
            !!pref?.preference || !!pref?.drinks || !!pref?.transport
        );
      if (hasPreference) {
        booked.add(String(dateValue));
      }
    }
    return booked;
  }

  private loadMealMenus(): void {
    const userId = Number(this.storedPayload?.userId);
    this.mealMenuAdminService
      .listMenus(Number.isFinite(userId) && userId > 0 ? userId : null)
      .subscribe({
        next: (response) => {
          const menus = response?.menus ?? [];
          if (!menus.length) {
            return;
          }
          this.dateOptions = applyMealMenusToDateOptions(
            this.dateOptions,
            menus,
            response?.mealDietPreference ?? null,
          ).map((option) => ({
            ...option,
            selected:
              this.dateOptions.find((d) => d.dateValue === option.dateValue)?.selected ??
              false,
            previouslyBooked:
              this.dateOptions.find((d) => d.dateValue === option.dateValue)
                ?.previouslyBooked ?? false,
          }));
        },
        error: () => {
          // Menus are optional for this picker.
        },
      });
  }
}
